/**
 * What a Session is measured against: the Character's derived maxima. Recomputed from the sheet
 * whenever it is needed, never stored in the Session, so a level-up or a new Effect changes the
 * caps at once.
 */
import type { IWRView } from "../engine/engine";
import type { ConsumableStack, Session } from "./model";
import { HERO_POINTS_MAX, SESSION_SCHEMA } from "./model";

export type CastingType = "prepared" | "spontaneous" | "innate" | "focus" | "items";

/** One Spellcasting Entry as the Session counts it (#10 produces these from the sheet). */
export interface SpellcastingSummary {
  id: string;
  name: string;
  casting: CastingType;
  /** By rank ("0" cantrips to "10"): the slot count, and for prepared Entries the spell UUID in each slot (null when empty). */
  slots: Record<string, { max: number; prepared?: (string | null)[] }>;
  /** Innate Entries: uses per day by spell UUID. */
  innate: Record<string, { max: number; name: string }>;
  /** Staff Entries (#21): the charges set at daily preparations and the spells they pay for, each costing its rank (cantrips are free). */
  charges?: { max: number; spells: { uuid: string; name: string; rank: number }[] };
}

export interface SessionMaxima {
  level: number;
  hp: {
    max: number;
    /** Foundry's rest recovery tweaks (`hp.recoveryMultiplier`, `hp.recoveryAddend`). */
    recoveryMultiplier: number;
    recoveryAddend: number;
  };
  /** The Constitution modifier, for daily preparations. */
  conMod: number;
  heroPoints: { max: number };
  focus: { max: number };
  /** Death track caps as the sheet derives them (dying max less doomed, wounded, doomed). */
  death: { dying: number; wounded: number; doomed: number };
  /** Special resources by key, with what renews them. */
  resources: Record<string, { label: string; max: number; renew: "daily" | false }>;
  /** Entries with a frequency the Character holds, by UUID. `per` is Foundry's: "day", "turn", "round" or an ISO duration. */
  uses: Record<string, { name: string; max: number; per: string }>;
  spellcasting: SpellcastingSummary[];
  /** The inventory's consumable and ammunition stacks (#11); a fresh Session starts from a copy. */
  consumables: ConsumableStack[];
  /** The conditions Pack by slug, for the Conditions the rules add on their own (dying, wounded). */
  conditions: Record<string, { uuid: string; name: string; valued: boolean }>;
  /** Fast healing in force (a number heals on each round; a formula needs a Roll). */
  fastHealing: { value: number | string; enabled: boolean; sourceName: string | null }[];
  /** Temporary HP granted at the start of each turn. */
  tempHPOnTurnStart: { value: number; enabled: boolean; sourceName: string | null }[];
  /** The sheet's immunities, weaknesses and resistances (#22), derived and entered by hand, applied to damage taken. */
  iwr: { immunities: IWRView[]; weaknesses: IWRView[]; resistances: IWRView[] };
  /** Void healing: vitality damage does not affect the Character, void damage does (Foundry's `isAffectedBy`). */
  negativeHealing: boolean;
}

/** Maxima for tests and for a Character with no sheet yet: everything zero but the caps. */
export function emptyMaxima(over: Partial<SessionMaxima> = {}): SessionMaxima {
  return {
    level: 1,
    hp: { max: 0, recoveryMultiplier: 1, recoveryAddend: 0 },
    conMod: 0,
    heroPoints: { max: HERO_POINTS_MAX },
    focus: { max: 0 },
    death: { dying: 4, wounded: 3, doomed: 4 },
    resources: {},
    uses: {},
    spellcasting: [],
    consumables: [],
    conditions: {},
    fastHealing: [],
    tempHPOnTurnStart: [],
    iwr: { immunities: [], weaknesses: [], resistances: [] },
    negativeHealing: false,
    ...over,
  };
}

/** A Session at the Character's derived maxima: full HP, one Hero Point, every slot and use fresh. */
export function freshSession(maxima: SessionMaxima, at: string): Session {
  return {
    schema: SESSION_SCHEMA,
    startedAt: at,
    clock: 0,
    round: null,
    hp: { value: maxima.hp.max, temp: 0 },
    conditions: [],
    effects: [],
    heroPoints: Math.min(1, maxima.heroPoints.max),
    focus: maxima.focus.max,
    spellcasting: Object.fromEntries(maxima.spellcasting.map((e) => [e.id, { slots: {}, prepared: {}, innate: {} }])),
    resources: Object.fromEntries(Object.entries(maxima.resources).map(([key, r]) => [key, r.max])),
    uses: {},
    consumables: maxima.consumables.map((s) => structuredClone(s)),
    toggles: {},
    counter: 1,
  };
}
